import type { ApiClient, Query } from '@saas/ui';
import type {
  CreateDelegationInput,
  CreateRequestInput,
  CreateRouteInput,
  CreateTemplateInput,
  RequestListQuery,
  RoutePreviewStep,
  UpdateRequestInput,
  UpdateRouteInput,
  UpdateTemplateInput,
  WorkflowAttachment,
  WorkflowDelegationSummary,
  WorkflowRequestDetail,
  WorkflowRequestSummary,
  WorkflowRetentionHit,
  WorkflowRouteSummary,
  WorkflowStampSummary,
  WorkflowTemplateSummary,
} from '../api/workflow.dto.js';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export type InboxRow = WorkflowRequestSummary & { canAct: true };
export type ListQuery = Partial<Omit<RequestListQuery, 'limit'>> & { limit?: number };

export const workflowKeys = {
  inboxCount: () => ['workflow', 'inbox', 'count'] as const,
  inbox: () => ['workflow', 'inbox', 'list'] as const,
  requests: (q: ListQuery) => ['workflow', 'requests', 'list', q] as const,
  request: (id: string) => ['workflow', 'requests', 'detail', id] as const,
  templates: () => ['workflow', 'templates'] as const,
  template: (code: string) => ['workflow', 'templates', code] as const,
  routes: () => ['workflow', 'routes'] as const,
  delegations: (all: boolean) => ['workflow', 'delegations', { all }] as const,
  attachments: (id: string) => ['workflow', 'requests', 'attachments', id] as const,
  stamp: () => ['workflow', 'stamps', 'me'] as const,
  retention: (q: RetentionQuery) => ['workflow', 'retention', q] as const,
};

/** 電帳法の 3 項目検索の条件（画面の入力そのまま） */
export interface RetentionQuery {
  dateFrom?: string;
  dateTo?: string;
  amountMin?: string;
  amountMax?: string;
  partner?: string;
  templateCode?: string;
  limit?: number;
}

export const workflowApi = {
  templates: (api: ApiClient) => api.get<WorkflowTemplateSummary[]>('/workflow/templates'),
  template: (api: ApiClient, code: string) => api.get<WorkflowTemplateSummary>(`/workflow/templates/${encodeURIComponent(code)}`),
  createTemplate: (api: ApiClient, body: CreateTemplateInput) => api.post<WorkflowTemplateSummary>('/workflow/templates', body),
  updateTemplate: (api: ApiClient, id: string, body: UpdateTemplateInput) => api.patch<WorkflowTemplateSummary>(`/workflow/templates/${id}`, body),
  installTemplate: (api: ApiClient, code: string) => api.post<WorkflowTemplateSummary>(`/workflow/templates/${encodeURIComponent(code)}/install`),
  previewTemplateRoute: (api: ApiClient, code: string, body: { applicantId?: string; amount?: string; data?: Record<string, unknown> }, signal?: AbortSignal) =>
    api.post<RoutePreviewStep[]>(`/workflow/templates/${encodeURIComponent(code)}/preview`, body, { signal }),

  routes: (api: ApiClient) => api.get<WorkflowRouteSummary[]>('/workflow/routes'),
  createRoute: (api: ApiClient, body: CreateRouteInput) => api.post<WorkflowRouteSummary>('/workflow/routes', body),
  updateRoute: (api: ApiClient, id: string, body: UpdateRouteInput) => api.patch<WorkflowRouteSummary>(`/workflow/routes/${id}`, body),
  deleteRoute: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/workflow/routes/${id}`),
  previewRoute: (api: ApiClient, id: string, body: { applicantId?: string; amount?: string; data?: Record<string, unknown> }) => api.post<RoutePreviewStep[]>(`/workflow/routes/${id}/preview`, body),

  requests: (api: ApiClient, q: ListQuery & { cursor?: string }) => api.get<Page<WorkflowRequestSummary>>('/workflow/requests', q as Query),
  exportCsv: (api: ApiClient, q: ListQuery) => api.download('/workflow/requests/export.csv', q as Query),
  request: (api: ApiClient, id: string) => api.get<WorkflowRequestDetail>(`/workflow/requests/${id}`),
  createRequest: (api: ApiClient, body: CreateRequestInput) => api.post<WorkflowRequestDetail>('/workflow/requests', body),
  updateRequest: (api: ApiClient, id: string, body: UpdateRequestInput) => api.patch<WorkflowRequestDetail>(`/workflow/requests/${id}`, body),
  deleteRequest: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/workflow/requests/${id}`),
  act: (api: ApiClient, id: string, action: 'submit' | 'approve' | 'reject' | 'withdraw' | 'pull-back', comment?: string) =>
    api.post<WorkflowRequestDetail>(`/workflow/requests/${id}/${action}`, comment ? { comment } : {}),
  return_: (api: ApiClient, id: string, body: { comment: string; to: 'applicant' | 'previous' }) => api.post<WorkflowRequestDetail>(`/workflow/requests/${id}/return`, body),
  attach: (api: ApiClient, id: string, fileIds: string[]) => api.post<WorkflowRequestDetail>(`/workflow/requests/${id}/attachments`, { fileIds }),

  attachments: (api: ApiClient, id: string) => api.get<WorkflowAttachment[]>(`/workflow/requests/${id}/attachments`),

  /** 電帳法の検索要件（日付・金額・取引先）で添付のある申請を探す */
  retentionSearch: (api: ApiClient, q: RetentionQuery & { cursor?: string }) => api.get<Page<WorkflowRetentionHit>>('/workflow/requests/retention-search', q as Query),

  /** 電子印影（本人だけ） */
  stamp: (api: ApiClient) => api.get<WorkflowStampSummary>('/workflow/stamps/me'),
  setStamp: (api: ApiClient, fileId: string) => api.put<WorkflowStampSummary>('/workflow/stamps/me', { fileId }),
  deleteStamp: (api: ApiClient) => api.del<WorkflowStampSummary>('/workflow/stamps/me'),

  inbox: (api: ApiClient, q: { limit?: number; cursor?: string }) => api.get<Page<InboxRow>>('/workflow/inbox', q),
  inboxCount: (api: ApiClient) => api.get<{ count: number }>('/workflow/inbox/count'),

  delegations: (api: ApiClient, all: boolean) => api.get<WorkflowDelegationSummary[]>('/workflow/delegations', all ? { all: true } : undefined),
  createDelegation: (api: ApiClient, body: CreateDelegationInput) => api.post<WorkflowDelegationSummary>('/workflow/delegations', body),
  deleteDelegation: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/workflow/delegations/${id}`),
};

/** 一覧の絞り込みは URL に持つ（戻るで復元できる） */
export function listQueryFromParams(sp: URLSearchParams): ListQuery {
  const status = sp.get('status');
  const mine = sp.get('mine');
  return {
    status: status && ['draft', 'pending', 'approved', 'rejected', 'withdrawn', 'cancelled'].includes(status) ? (status as RequestListQuery['status']) : undefined,
    templateCode: sp.get('template') || undefined,
    mine: mine === 'applicant' || mine === 'approver' ? mine : undefined,
    q: sp.get('q') || undefined,
    from: sp.get('from') || undefined,
    to: sp.get('to') || undefined,
    limit: 50,
  };
}
